import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";
import { MarketingLayout } from "@/components/MarketingLayout";
import client from "@/lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    client.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <MarketingLayout>
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl font-bold">Post not found</h1>
          <Link to="/blog" className="mt-4 inline-block text-ho-primary">← Back to blog</Link>
        </div>
      </MarketingLayout>
    );
  }

  if (!post) {
    return (
      <MarketingLayout>
        <div className="grid min-h-[50vh] place-items-center">
          <div className="w-8 h-8 rounded-full border-[3px] border-ho-primary border-t-transparent animate-spin" />
        </div>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <article className="mx-auto max-w-3xl px-6 pt-10 pb-16">
        <Link to="/blog" data-testid="blog-back"
          className="tap-scale inline-flex items-center gap-1.5 text-sm font-semibold text-ho-muted hover:text-ho-ink">
          <ArrowLeft className="w-4 h-4" /> All posts
        </Link>

        <div className="mt-6 flex flex-wrap gap-2">
          {post.tags?.map((t) => (
            <span key={t} className="rounded-full bg-ho-primary/10 px-3 py-1 text-xs font-semibold text-ho-primary">{t}</span>
          ))}
        </div>
        <h1 className="font-display mt-4 text-4xl font-black tracking-tighter md:text-5xl">{post.title}</h1>
        <div className="mt-5 flex items-center gap-4 text-sm text-ho-muted">
          <span>{post.author}</span>
          <span>·</span>
          <span>{new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.read}</span>
        </div>

        <img src={post.cover} alt={post.title} className="mt-8 aspect-[16/9] w-full rounded-[28px] border border-black/5 object-cover" />

        <div className="mt-8 space-y-5">
          {post.body.map((para, i) =>
            para.startsWith("## ") ? (
              <h2 key={i} className="font-display pt-2 text-2xl font-bold tracking-tight">{para.replace("## ", "")}</h2>
            ) : (
              <p key={i} className="text-[17px] leading-relaxed text-ho-ink/85">{para}</p>
            )
          )}
        </div>

        <div className="mt-12 rounded-[28px] bg-ho-ink p-8 text-center text-white">
          <h3 className="font-display text-2xl font-bold">Try it yourself</h3>
          <p className="mt-2 text-white/70">Build your first site free — right now, on this phone.</p>
          <button onClick={() => navigate("/login")} data-testid="blogpost-cta"
            className="tap-scale mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-ho-ink">
            Start building <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </article>
    </MarketingLayout>
  );
}
